import { useId, useRef, useState } from 'react'
import { Paperclip, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { createTicketSender } from '@/lib/supportTicket'
import {
  ATTACHMENT_ACCEPT, ATTACHMENT_MAX_COUNT, DETAILS_MAX, TICKET_TYPES, TICKET_URGENCIES, TITLE_MAX,
  TYPE_LABELS, URGENCY_LABELS, checkAttachments, formatBytes,
  type TicketDraft, type TicketType, type TicketUrgency,
} from '@/lib/supportTicketCore'

// Ask Rize's draft ticket, under the answer that drafted it. The assistant only
// WROTE this — nothing leaves until the person presses Send (spec:
// docs/superpowers/specs/2026-10-10-ask-rize-support-tickets-design.md). Every
// field is editable, and a failed send keeps the edits so they can retry.
// Native <select>s on purpose: a portalled Radix Select would open UNDER the
// panel's z-[80] overlay.

interface SupportTicketCardProps {
  draft: TicketDraft
  organizationId: string | null
  onSent: (reference: string) => void
  onDismiss: () => void
}

const SELECT_CLASS =
  'flex min-h-[44px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card'

export function SupportTicketCard({ draft, organizationId, onSent, onDismiss }: SupportTicketCardProps) {
  const id = useId()
  const [title, setTitle] = useState(draft.title)
  const [description, setDescription] = useState(draft.description)
  const [type, setType] = useState<TicketType>(draft.type)
  const [urgency, setUrgency] = useState<TicketUrgency>(draft.urgency)
  const [files, setFiles] = useState<File[]>([])
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [progress, setProgress] = useState('')
  const [sending, setSending] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  // One sender for the card's life (lazy initialiser): it keeps the upload session, so a retry
  // doesn't re-upload a recording.
  const [sendTicket] = useState(() => createTicketSender())

  const canSend = !sending && !!organizationId && title.trim().length > 0 && description.trim().length > 0

  function addFiles(list: FileList | null) {
    if (!list || list.length === 0) return
    const { accepted, refused } = checkAttachments(Array.from(list), ATTACHMENT_MAX_COUNT - files.length)
    if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted].slice(0, ATTACHMENT_MAX_COUNT))
    setNotice(refused.join(' '))
    if (fileRef.current) fileRef.current.value = ''
  }

  async function send() {
    if (!canSend || !organizationId) return
    setSending(true)
    setError('')
    try {
      const { reference } = await sendTicket({
        organizationId,
        draft: { type, urgency, title: title.trim(), description: description.trim() },
        files,
        onProgress: setProgress,
      })
      onSent(reference)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Couldn’t send the ticket. Try again in a minute.')
    } finally {
      setSending(false)
      setProgress('')
    }
  }

  return (
    <section aria-label="Draft support ticket" className="w-full rounded-lg border border-primary/30 bg-background p-3">
      <h3 className="text-sm font-semibold">Draft ticket for the Rize OS support team</h3>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Check it and change anything you like. Nothing is sent until you press Send.
      </p>

      <div className="mt-3 flex flex-col gap-3">
        {/* Locked while sending: an edit typed after the click would not be what was sent. */}
        <fieldset disabled={sending} className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${id}-title`} className="text-xs">Title</Label>
            <Input
              id={`${id}-title`}
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              className="min-h-[44px]"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor={`${id}-details`} className="text-xs">Details</Label>
            <Textarea
              id={`${id}-details`}
              value={description}
              maxLength={DETAILS_MAX}
              rows={5}
              onChange={(e) => setDescription(e.target.value)}
              className="resize-y"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${id}-type`} className="text-xs">Kind</Label>
              <select id={`${id}-type`} value={type} onChange={(e) => setType(e.target.value as TicketType)} className={SELECT_CLASS}>
                {TICKET_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${id}-urgency`} className="text-xs">Urgency</Label>
              <select id={`${id}-urgency`} value={urgency} onChange={(e) => setUrgency(e.target.value as TicketUrgency)} className={SELECT_CLASS}>
                {TICKET_URGENCIES.map((u) => <option key={u} value={u}>{URGENCY_LABELS[u]}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept={ATTACHMENT_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            {files.length > 0 && (
              <ul className="flex flex-col gap-1" aria-label="Attached files">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 rounded-md border border-border pl-2 text-xs">
                    <span className="truncate">{f.name}</span>
                    <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
                      {formatBytes(f.size)}
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        aria-label={`Remove ${f.name}`}
                        className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {files.length < ATTACHMENT_MAX_COUNT && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={sending}
                className="min-h-[44px] self-start"
              >
                <Paperclip className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                Attach screenshots or a recording
              </Button>
            )}
            {notice && <p role="status" className="text-xs text-muted-foreground">{notice}</p>}
          </div>
        </fieldset>

        {sending && progress && <p role="status" className="text-xs text-muted-foreground">{progress}</p>}
        {error && <p role="alert" className="text-xs text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onDismiss} disabled={sending} className="min-h-[44px]">
            Cancel
          </Button>
          <Button type="button" onClick={() => void send()} disabled={!canSend} className="min-h-[44px]">
            {sending ? 'Sending…' : 'Send to support'}
          </Button>
        </div>
      </div>
    </section>
  )
}
